
import { markRaw } from 'vue';
import { Component, Vue } from 'vue-property-decorator';
import { get } from '@/services/http';
import {
  isPickerInitEmail,
  pickedFileDocs,
  pickerFilesResult,
  pickerReady,
  pickerResult,
  readMimeTypes,
  readPickerMode,
  resolveAtlasOrigin,
  shouldRebuildOnReturn,
  googleSignInUrl,
  PickerMode,
  REBUILD_CANCEL_GRACE_MS,
} from '@/utils/googleDrivePickerBridge';

declare const gapi: any;
declare const google: any;

/**
 * Top-level host for the Google Drive picker, opened via window.open from the
 * embedded Atlas iframe. Hosted on the warbler origin because the Google Picker
 * renders only for origins registered on our OAuth client — warbler's are,
 * Atlas's are not. See utils/googleDrivePickerBridge.ts.
 *
 * Serves two modes over one channel: 'folders' (scheduled-report destinations)
 * and 'files' (PO invoice attachments, multi-select + MIME-filtered).
 */
@Component
export default class GoogleDrivePicker extends Vue {
  public status: string = 'Connecting to Google Drive…';
  public pickerShown: boolean = false;
  public picked: boolean = false;
  // Public: the reload bar names the account the picker must be signed in as.
  public email: string = '';
  private atlasOrigin: string = resolveAtlasOrigin(process.env.VUE_APP_ATLAS_FRONTEND_URL);
  private boundOnMessage: ((e: MessageEvent) => void) | null = null;
  private opened: boolean = false;
  private mode: PickerMode = 'folders';
  private mimeTypes: string[] = [];
  private token: string = '';
  private signinWin: Window | null = null;
  private pickerObj: any = null;
  private rebuilding: boolean = false;
  private rebuildGraceUntil: number = 0;
  private lastBuildAt: number = 0;
  private lastHadFocus: boolean = false;
  private lastManualAt: number = 0;
  private pollTimer: number | null = null;

  public mounted() {
    const opener = window.opener as Window | null;
    if (!opener || opener === window) {
      this.status = 'Open this window from Assisty to choose a Drive folder.';
      return;
    }
    this.boundOnMessage = (e: MessageEvent) => { this.handleMessage(e); };
    window.addEventListener('message', this.boundOnMessage);
    opener.postMessage(pickerReady(), this.atlasOrigin);
  }

  public beforeDestroy() {
    this.stopFocusPoll();
    this.closeSignInWin();
    if (this.boundOnMessage) {
      window.removeEventListener('message', this.boundOnMessage);
    }
  }

  /**
   * Steered sign-in: opens the account chooser pre-selected to the token's
   * account in a window WE own — unlike the picker's inner sign-in link,
   * whose Google-owned continuation window we can never close.
   */
  public openSignIn() {
    this.closeSignInWin();
    this.signinWin = window.open(
      googleSignInUrl(this.email),
      'assisty-google-signin',
      'width=520,height=640,scrollbars=yes,resizable=yes',
    );
  }

  /**
   * Manual fallback for when the focus heuristic misses. 1s debounce.
   */
  public manualReload() {
    const now = Date.now();
    if (now - this.lastManualAt < 1000) { return; }
    this.lastManualAt = now;
    // Fire-and-forget: a rejection has nowhere to go here, and the picker
    // already on screen stays usable.
    this.rebuildPicker().catch(() => undefined);
  }

  private closeSignInWin() {
    try {
      if (this.signinWin && !this.signinWin.closed) { this.signinWin.close(); }
    } catch (err) {
      // A dead or foreign handle must not break the picker flow.
    }
    this.signinWin = null;
  }

  private async handleMessage(e: MessageEvent) {
    // Only the embedded Atlas document that opened this popup may drive it.
    if (e.origin !== this.atlasOrigin || e.source !== window.opener) { return; }
    if (!isPickerInitEmail(e.data) || this.opened) { return; }
    this.opened = true;
    this.mode = readPickerMode(e.data.payload);
    this.mimeTypes = readMimeTypes(e.data.payload);
    this.email = e.data.payload.email;

    let token: string = '';
    try {
      const response: any = await get('/token/google_oauth/access_token', { email: e.data.payload.email });
      token = response.data.access_token || '';
    } catch (err) {
      token = '';
    }
    if (!token) {
      this.status = 'Could not authorize with Google. Sign in to Assisty in this browser, then close this window and retry.';
      return;
    }
    this.token = token;
    gapi.load('picker', { callback: () => { this.openPicker(token); } });
  }

  private openPicker(token: string) {
    const builder = new google.picker.PickerBuilder()
        .setOAuthToken(token)
        .setCallback((data: any) => { this.onPicked(data); })
        .setOrigin(window.location.origin);

    if (this.mode === 'files') {
      this.status = 'Choose files…';
      builder
          .addView(new google.picker.DocsView(google.picker.ViewId.DOCS))
          // Invoices are routinely attached in batches, and the opener's
          // callback already takes an array.
          .enableFeature(google.picker.Feature.MULTISELECT_ENABLED);
      // An empty list means "no filter" — better to show every file than to
      // hand the API an empty string and show none.
      if (this.mimeTypes.length > 0) {
        builder.setSelectableMimeTypes(this.mimeTypes.join(','));
      }
    } else {
      this.status = 'Choose a folder…';
      const folderView = new google.picker.DocsView(google.picker.ViewId.FOLDERS)
          .setParent('root')
          .setSelectFolderEnabled(true);
      builder
          .addView(folderView)
          .setSelectableMimeTypes('application/vnd.google-apps.folder');
    }

    // markRaw, or Vue's observer deep-walks the gapi Picker held in this
    // initialized field (vue-class-component puts it in data()) and rewrites
    // its properties as getters/setters.
    this.pickerObj = markRaw(builder.build());
    this.pickerObj.setVisible(true);
    this.pickerShown = true;
    this.lastBuildAt = Date.now();
    this.startFocusPoll();
  }

  private onPicked(data: any) {
    const opener = window.opener as Window | null;
    if (data.action === google.picker.Action.PICKED) {
      if (this.mode === 'files') {
        const files = pickedFileDocs(data.docs);
        if (files.length === 0) { return; }
        this.picked = true;
        this.stopFocusPoll();
        if (opener) {
          opener.postMessage(pickerFilesResult(files), this.atlasOrigin);
        }
        this.closeSignInWin();
        window.close();
        return;
      }
      if (data.docs && data.docs[0]) {
        const folder = data.docs[0];
        this.picked = true;
        this.stopFocusPoll();
        if (opener) {
          opener.postMessage(pickerResult(folder.id, folder.name), this.atlasOrigin);
        }
        this.closeSignInWin();
        window.close();
      }
    } else if (data.action === google.picker.Action.CANCEL) {
      // Disposing the old picker during a rebuild provokes a CANCEL that may
      // land after the rebuild finished; only a real user cancel may close the
      // window, so ignore anything inside the post-dispose grace window.
      if (Date.now() < this.rebuildGraceUntil) { return; }
      this.closeSignInWin();
      window.close();
    }
  }

  /**
   * After the merchant signs in to Google in another window, the picker here
   * is stuck on its sign-in shell and Google's continuation window cannot
   * deliver a pick (RPC relay origin mismatch — see the spec). Rebuilding
   * with a fresh token renders the authenticated picker where the relay works.
   */
  private async rebuildPicker() {
    if (!this.opened || this.picked || this.rebuilding) { return; }
    this.rebuilding = true;
    try {
      this.status = 'Refreshing the picker…';
      let token = this.token;
      try {
        const response: any = await get('/token/google_oauth/access_token', { email: this.email });
        token = response.data.access_token || token;
      } catch (err) {
        // Keep the previous token — never downgrade a working state.
      }
      // The old picker stayed clickable across that await. If the merchant
      // picked meanwhile, onPicked has already posted the result, stopped the
      // poll and closed — rebuilding now would re-arm the cleared interval.
      if (this.picked) { return; }
      // The rebuild is the merchant's return trip: our sign-in window has done
      // its job, and the picker needs the focus it is about to be rebuilt with.
      this.closeSignInWin();
      window.focus();
      this.token = token;
      // Opened BEFORE the dispose, and outliving this method: the CANCEL the
      // dispose provokes can arrive a tick later, once rebuilding is false.
      this.rebuildGraceUntil = Date.now() + REBUILD_CANCEL_GRACE_MS;
      try {
        if (this.pickerObj) {
          if (typeof this.pickerObj.setVisible === 'function') { this.pickerObj.setVisible(false); }
          if (typeof this.pickerObj.dispose === 'function') { this.pickerObj.dispose(); }
        }
      } catch (err) {
        // A failed dispose must not block the rebuild.
      }
      this.openPicker(token);
    } finally {
      // Must clear even if the builder throws (gapi unloaded, setVisible
      // failure): a stuck flag would early-return every later rebuild and
      // leave the popup with no working way out.
      this.rebuilding = false;
    }
  }

  private startFocusPoll() {
    if (this.pollTimer !== null) { return; }
    this.lastHadFocus = document.hasFocus();
    this.pollTimer = window.setInterval(() => {
      const hasFocus = document.hasFocus();
      const decide = shouldRebuildOnReturn({
        prevHasFocus: this.lastHadFocus,
        hasFocus,
        msSinceLastBuild: Date.now() - this.lastBuildAt,
        picked: this.picked,
      });
      this.lastHadFocus = hasFocus;
      // Fire-and-forget: an unhandled rejection must not escape the interval.
      if (decide) { this.rebuildPicker().catch(() => undefined); }
    }, 1000);
  }

  private stopFocusPoll() {
    if (this.pollTimer !== null) {
      window.clearInterval(this.pollTimer);
      this.pollTimer = null;
    }
  }
}
